import React from 'react';
import { Box, List, ListItemButton, Typography } from '@mui/material';
import { Folder } from 'lucide-react';
import type { DocumentCategory } from '@/services/api/documentService';
import { ALL_FOLDERS_ID, flattenCategoryTree } from '../documentUi';

interface DocumentsSidebarProps {
  categories: DocumentCategory[];
  selectedId: string | null;
  totalFiles: number;
  canManageCategories?: boolean;
  onSelect: (id: string) => void;
}

const DocumentsSidebar: React.FC<DocumentsSidebarProps> = ({
  categories,
  selectedId,
  totalFiles,
  canManageCategories = false,
  onSelect,
}) => {
  const tree = flattenCategoryTree(categories);
  const allSelected = selectedId === ALL_FOLDERS_ID;

  return (
    <Box sx={{ px: 1.5, py: 2 }}>
      <Typography
        variant="caption"
        sx={{ px: 1.5, mb: 1, display: 'block', letterSpacing: 1, color: 'text.secondary', fontWeight: 600 }}
      >
        FOLDERS
      </Typography>
      <List disablePadding>
        <FolderRow
          label="All Documents"
          count={totalFiles}
          depth={0}
          selected={allSelected}
          muted={false}
          onClick={() => onSelect(ALL_FOLDERS_ID)}
        />
        {tree.map(({ cat, depth }) => (
          <FolderRow
            key={cat.id}
            label={
              cat.pathOnly
                ? cat.name
                : cat.isRestricted
                  ? `${cat.name} (shared)`
                  : cat.name
            }
            count={cat.pathOnly ? 0 : cat.documentCount}
            depth={depth}
            selected={cat.id === selectedId}
            muted={!cat.isActive || Boolean(cat.pathOnly)}
            onClick={() => onSelect(cat.id)}
          />
        ))}
      </List>
      {categories.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, mt: 1 }}>
          {canManageCategories
            ? 'No folders yet — create one and share it with users.'
            : 'No folders shared with you yet.'}
        </Typography>
      )}
    </Box>
  );
};

const FolderRow: React.FC<{
  label: string;
  count: number;
  depth: number;
  selected: boolean;
  muted: boolean;
  onClick: () => void;
}> = ({ label, count, depth, selected, muted, onClick }) => (
  <ListItemButton
    onClick={onClick}
    selected={selected}
    sx={{
      mb: 0.5,
      ml: depth * 1.5,
      borderRadius: 2,
      py: 1.1,
      px: 1.5,
      color: selected ? '#fff' : muted ? 'text.disabled' : 'text.secondary',
      '&.Mui-selected': {
        bgcolor: 'primary.main',
        color: '#fff',
        '&:hover': { bgcolor: 'primary.dark' },
      },
      '&:hover': { bgcolor: selected ? 'primary.dark' : 'grey.100' },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
      <Folder size={16} />
      <Typography noWrap fontWeight={selected ? 600 : 500} fontSize="0.875rem">
        {label}{muted ? ' (hidden)' : ''}
      </Typography>
    </Box>
    <Box
      sx={{
        minWidth: 24,
        height: 22,
        px: 0.75,
        borderRadius: 10,
        fontSize: '0.7rem',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: selected ? '#fff' : 'grey.200',
        color: selected ? 'primary.main' : 'text.secondary',
      }}
    >
      {count}
    </Box>
  </ListItemButton>
);

export default DocumentsSidebar;
